<template>
  <Primitive
    :as="variant === 'select' ? ListboxItem : 'div'"
    :class="
      cn(
        'flex items-center py-2 px-3 gap-2 bg-primary data-[highlighted]:bg-primary_hover rounded-md shadow-xs',
        isExpired(file.expiration_date)
          ? 'text-error-primary'
          : 'text-secondary',
        { 'cursor-pointer': variant === 'select' },
        props.class
      )
    "
  >
    <FileType
      size="sm"
      variant="solid"
      :extension="getExtension(file.file.name)"
    />
    <div class="flex flex-col grow overflow-x-hidden">
      <span
        class="w-full font-medium truncate"
        :class="{
          'text-style-base': viewport !== 'desktop',
          'text-style-sm': viewport === 'desktop',
          'lg:text-style-sm': viewport === 'reactive',
        }"
      >
        {{ file.title }}
      </span>
    </div>
    <div
      v-if="variant !== 'select' && file.expiration_date"
      class="h-5.5 flex items-center justify-center px-0.5 gap-1"
    >
      <Icon name="calendar-default" :size="isMobile ? 'md' : 'sm'" />
      <span
        class="font-normal"
        :class="{
          'text-style-sm': viewport !== 'desktop',
          'text-style-xs': viewport === 'desktop',
          'lg:text-style-xs': viewport === 'reactive',
        }"
      >
        {{ formatDate(file.expiration_date, { dateStyle: "short" }) }}
      </span>
    </div>
    <div class="flex items-center">
      <Checkbox
        v-if="variant === 'select'"
        size="sm"
        :checked="checked"
        :decorative="true"
      />
      <Actions
        v-else
        :file="file.file"
        :preview="file.preview"
        :size="isMobile ? 'sm' : 'xs'"
        :canUpdate="canUpdate"
        @preview="$emit('preview', file.id)"
        @download="downloadFile"
        @unlink="$emit('unlink', file)"
      />
    </div>
  </Primitive>
</template>

<script setup>
import { computed } from "vue";
import { ListboxItem, Primitive } from "radix-vue";
import { Checkbox } from "@/SharedComponents/Checkboxes";
import { FileType } from "@/SharedComponents/File";
import { Icon } from "@/SharedComponents/Icon";
import { Actions } from "@/SharedComponents/Passport/File";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useDate } from "@/Composables/useDate";
import { useWebView } from "@/Composables/useWebView";
import { mobileSatisfiesVersion } from "@/Utils/version";
import { getExtension, isExpired } from "@/Utils/files";
import { cn } from "@/Utils/cn";

defineEmits(["preview", "unlink"]);

const props = defineProps({
  variant: { type: String, required: false },
  file: { type: Object, required: true },
  checked: { type: Boolean, required: false },
  canUpdate: { type: Boolean, required: false },
  viewport: {
    type: String,
    validator: (val) => ["mobile", "desktop", "reactive"].includes(val),
    default: "reactive",
  },
  class: { type: null, required: false },
});

const { formatDate } = useDate();

const { runningInWebView, openURL } = useWebView();

function downloadFile(url) {
  if (!runningInWebView()) {
    window.open(url, "_blank");
    return;
  }

  if (mobileSatisfiesVersion("2.2.0")) {
    openURL(url);
    return;
  }

  // Fallback for outdated mobile apps
  window.open(url, "_blank");
}

const { maxTablet } = useBreakpoints();

const isMobile = computed(() => {
  switch (props.viewport) {
    case "mobile":
      return true;
    case "desktop":
      return false;
    case "reactive":
    default:
      return maxTablet.value;
  }
});
</script>
